import React from "react";
import { ProductResponse } from "@/app/api/types/product";
import { useTranslation } from "react-i18next";
import { UUID } from "crypto";
import { useProductDisplayName } from "@/hooks/use-product-display-name";

interface VariantsSubmenuProps {
  hoveredProductId: UUID | null;
  submenuPosition: { top: number; left: number } | null;
  allProducts: ProductResponse[];
  allProductsWithChildren: ProductResponse[];
  getProductChildren: (productId: UUID) => ProductResponse[];
  onProductSelect: (
    product: ProductResponse,
    subproductId?: UUID | null,
  ) => void;
  setHoveredProductId: (id: UUID | null) => void;
  setSubmenuPosition: (pos: { top: number; left: number } | null) => void;
}

export const VariantsSubmenu = ({
  hoveredProductId,
  submenuPosition,
  allProducts,
  allProductsWithChildren,
  getProductChildren,
  onProductSelect,
  setHoveredProductId,
  setSubmenuPosition,
}: VariantsSubmenuProps) => {
  const { t } = useTranslation();
  const displayName = useProductDisplayName();

  if (!hoveredProductId || !submenuPosition) return null;

  let product = allProducts.find((p) => p.id === hoveredProductId);

  if (!product) {
    for (const p of allProductsWithChildren) {
      if (p.children) {
        const childMatch = p.children.find((c) => c.id === hoveredProductId);
        if (childMatch) {
          product = childMatch;
          break;
        }
      }
    }
  }

  if (!product) return null;

  const isVariant = (product.parents?.length ?? 0) > 0;
  const parentProduct = isVariant ? product.parents?.[0] : null;

  let variants: ProductResponse[] = [];
  let displayProduct = product;

  if (isVariant && parentProduct) {
    let fullParent = allProductsWithChildren.find(
      (p) => p.id === parentProduct.id,
    );

    if (!fullParent) {
      fullParent = allProductsWithChildren.find((p) =>
        p.children?.some((c) => c.id === product!.id),
      );
    }

    if (fullParent) {
      displayProduct = fullParent;
      variants = (fullParent.children ?? []).filter(
        (child) => child.is_user_product !== false,
      );
    } else {
      variants = (parentProduct.children ?? []).filter(
        (child) => child.is_user_product !== false,
      );
    }
  } else {
    variants = getProductChildren(hoveredProductId);
  }

  if (!isVariant && variants.length === 0) return null;

  return (
    <div
      className="fixed z-[10000] bg-white border border-gray-200 rounded-md shadow-lg overflow-hidden min-w-[200px]"
      style={{
        top: `${submenuPosition.top}px`,
        left: `${submenuPosition.left}px`,
        maxHeight: "20rem",
      }}
      onMouseEnter={() => {
        setHoveredProductId(hoveredProductId);
      }}
      onMouseLeave={() => {
        setHoveredProductId(null);
        setSubmenuPosition(null);
      }}
    >
      <div className="max-h-80 overflow-y-auto">
        {/* Option to select parent product (without variant) */}
        <div
          className="px-3 py-2 hover:bg-gray-50 cursor-pointer border-b border-gray-100"
          onMouseDown={(e) => {
            e.preventDefault();
            onProductSelect(displayProduct, null);
          }}
        >
          <div className="text-xs text-gray-500">
            {isVariant
              ? t("ordersRecap.parentProduct", "Parent product")
              : t("ordersRecap.noVariant", "No variant")}
          </div>
          <div className="text-sm font-medium text-gray-700">
            {displayName(displayProduct)}
          </div>
        </div>

        {/* Variant options */}
        {variants.map((variant) => (
          <div
            key={variant.id}
            className="px-3 py-2 hover:bg-blue-50 cursor-pointer"
            onMouseDown={(e) => {
              e.preventDefault();
              onProductSelect(displayProduct, variant.id);
            }}
          >
            <div className="text-sm font-medium text-gray-900">
              {displayName(variant)}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
